import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Paper, TextField } from '@mui/material';
import _ from 'lodash';

import {
  agencyUserRoleSelector,
  brokerAgencySelector,
  isLoadingBrokerAgencySelector,
  updateBrokerAgency,
} from 'src/store/agency';

import { UploadImage } from 'src/features/uploadImage/UploadImage';

import { Button, LoaderWrapper, LogoLoader } from 'src/components';

import classes from './agencyProfile.module.scss';

export const AgencyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const agencyData = useSelector(brokerAgencySelector);
  const isLoadingAgencyData = useSelector(isLoadingBrokerAgencySelector);
  const agencyUserRole = useSelector(agencyUserRoleSelector);

  const [isEdit, setIsEdit] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [agencyAddress, setAgencyAddress] = useState('');
  const [agencyCity, setAgencyCity] = useState('');
  const [agencyState, setAgencyState] = useState('');
  const [agencyZipCode, setAgencyZipCode] = useState('');

  const [agencyLogo, setAgencyLogo] = useState(agencyData?.logo_url);

  useEffect(() => {
    if (!_.isEmpty(agencyData)) {
      setAgencyLogo(agencyData?.logo_url);
      setAgencyName(agencyData?.name);
      setAgencyAddress(agencyData?.address_street);
      setAgencyCity(agencyData?.address_city);
      setAgencyState(agencyData?.address_state);
      setAgencyZipCode(agencyData?.address_zip_code);
    }
  }, [agencyData]);

  useEffect(() => {
    if (!_.isEmpty(agencyData) && agencyData.status === 'inactive') {
      navigate(`/teams/${agencyData.id}/settings?activeTab=billing`);
    }
  }, [agencyData, navigate]);

  const onSetAgencyLogo = useCallback((image) => {
    setAgencyLogo(image);
  }, []);

  const onChangeInput = useCallback(
    (setValue) => (event) => {
      setValue(event.target.value);
    },
    [],
  );

  const onClickEdit = useCallback(() => {
    setIsEdit(true);
  }, []);

  const onSaveEdit = useCallback(() => {
    const payload = {
      name: agencyName,
      address_street: agencyAddress,
      address_city: agencyCity,
      address_state: agencyState,
      address_zip_code: agencyZipCode,
      logo_url: agencyLogo,
    };

    dispatch(updateBrokerAgency({ payload, id: agencyData?.id }));

    setIsEdit(false);
  }, [
    dispatch,
    agencyData,
    agencyAddress,
    agencyCity,
    agencyName,
    agencyState,
    agencyZipCode,
    agencyLogo,
  ]);

  const onClickCancel = useCallback(() => {
    setAgencyLogo(agencyData?.logo_url);
    setAgencyName(agencyData?.name);
    setAgencyAddress(agencyData?.address_street);
    setAgencyCity(agencyData?.address_city);
    setAgencyState(agencyData?.address_state);
    setAgencyZipCode(agencyData?.address_zip_code);

    setIsEdit(false);
  }, [agencyData]);

  return (
    <div className={classes.AgencyProfile}>
      <Paper sx={{ padding: 2 }}>
        {isLoadingAgencyData ? (
          <LoaderWrapper>
            <LogoLoader />
          </LoaderWrapper>
        ) : (
          <div className={classes.AgencyProfileContent}>
            <UploadImage
              path={`users/${agencyData?.id}/avatar`}
              isOnlyImage={!isEdit}
              previewImage={agencyLogo}
              setPreviewImage={onSetAgencyLogo}
              type="profile"
              isCirclePreviewImage
            />

            {/* RFPS draft and completed acknowledge */}

            <TextField
              size="small"
              label="Organization Name"
              value={agencyName}
              onChange={onChangeInput(setAgencyName)}
              disabled={!isEdit}
              sx={{ width: '100%' }}
            />
            <div className={classes.AgencyProfileGrid}>
              <TextField
                size="small"
                label="Address"
                value={agencyAddress}
                onChange={onChangeInput(setAgencyAddress)}
                disabled={!isEdit}
              />
              <TextField
                size="small"
                label="City"
                value={agencyCity}
                onChange={onChangeInput(setAgencyCity)}
                disabled={!isEdit}
              />
              <TextField
                size="small"
                label="State"
                value={agencyState}
                onChange={onChangeInput(setAgencyState)}
                disabled={!isEdit}
              />

              <TextField
                size="small"
                label="Zip Code"
                value={agencyZipCode}
                type="number"
                onChange={onChangeInput(setAgencyZipCode)}
                disabled={!isEdit}
              />
            </div>

            <div className={classes.AgencyProfileFooterButton}>
              {!isEdit && agencyUserRole === 'owner' && (
                <Button title="Edit" type="secondary" onClick={onClickEdit} />
              )}

              {isEdit && (
                <>
                  <Button title="Save" type="primary" onClick={onSaveEdit} />
                  <Button title="Cancel" type="secondary" onClick={onClickCancel} />
                </>
              )}
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};
